import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

export interface Student {
  user_id: string;
  email: string;
  name: string;
  tags?: string[];
}

interface StudentContextType {
  students: Student[];
  dfpbStudents: Student[];
  loading: boolean;
  error: string | null;
  refreshStudents: () => Promise<void>;
}

const StudentContext = createContext<StudentContextType | undefined>(undefined);

export function useStudents() {
  const context = useContext(StudentContext);
  if (!context) {
    throw new Error('useStudents must be used within a StudentProvider');
  }
  return context;
}

interface StudentProviderProps {
  children: ReactNode;
}

export function StudentProvider({ children }: StudentProviderProps) {
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ws, setWs] = useState<WebSocket | null>(null);

  // Fetch DFPB students using optimized endpoint
  const fetchStudents = async () => {
    console.log('[StudentContext] fetchStudents called');
    try {
      setLoading(true);
      setError(null);

      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token');
      }

      const url = `${import.meta.env.VITE_API_URL}/admin/users?tag=DFPB`;
      console.log('[StudentContext] Fetching from:', url);

      // Use optimized query parameter to only return DFPB students (tagged with 'DFPB')
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('[StudentContext] Response status:', response.status);

      if (!response.ok) {
        throw new Error('Failed to fetch DFPB students (tag: DFPB)');
      }

      const data = await response.json();
      console.log('[StudentContext] Received data:', data);

      const dfpbStudents = (data.users || [])
        .map((user: any) => ({
          user_id: user.user_id,
          email: user.email,
          name: user.name || user.email,
          tags: user.tags || []
        }))
        .sort((a: Student, b: Student) => a.name.localeCompare(b.name));

      console.log('[StudentContext] Processed students:', dfpbStudents);
      setStudents(dfpbStudents);
    } catch (err) {
      console.error('[StudentContext] Error fetching DFPB students:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch DFPB students');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  // Set up WebSocket connection
  const setupWebSocket = async () => {
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();

      if (!accessToken) return;

      // WebSocket URL from environment
      const wsUrl = import.meta.env.VITE_WEBSOCKET_URL;
      if (!wsUrl) {
        console.warn('WebSocket URL not configured');
        return;
      }

      const socket = new WebSocket(`${wsUrl}?token=${accessToken}`);

      socket.onopen = () => {
        console.log('WebSocket connected');
      };

      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleWebSocketMessage(message);
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      socket.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      socket.onclose = () => {
        console.log('WebSocket disconnected');
        // Reconnect after 5 seconds
        setTimeout(() => {
          setupWebSocket();
        }, 5000);
      };

      setWs(socket);
    } catch (err) {
      console.error('Error setting up WebSocket:', err);
    }
  };

  // Handle WebSocket messages
  const handleWebSocketMessage = (message: any) => {
    const { type, data } = message;

    switch (type) {
      case 'user.created':
      case 'user.updated':
        // Add or update user in the list
        const updatedUser: Student = {
          email: data.email,
          name: data.name || data.email,
          tags: data.tags || []
        };

        setStudents(prev => {
          const index = prev.findIndex(s => s.email === updatedUser.email);
          if (index >= 0) {
            // Update existing
            const updated = [...prev];
            updated[index] = updatedUser;
            return updated.sort((a, b) => a.name.localeCompare(b.name));
          } else {
            // Add new
            return [...prev, updatedUser].sort((a, b) => a.name.localeCompare(b.name));
          }
        });
        break;

      case 'user.deleted':
        // Remove user from the list
        setStudents(prev => prev.filter(s => s.email !== data.email));
        break;

      default:
        console.log('Unknown WebSocket message type:', type);
    }
  };

  // Initial load
  useEffect(() => {
    fetchStudents();
    setupWebSocket();

    // Cleanup WebSocket on unmount
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, []);

  // students already contains ONLY DFPB students from optimized endpoint
  // No need to filter again - backend already filtered by DFPB tag
  const value: StudentContextType = {
    students,
    dfpbStudents: students,
    loading,
    error,
    refreshStudents: fetchStudents
  };

  return (
    <StudentContext.Provider value={value}>
      {children}
    </StudentContext.Provider>
  );
}
